import { ArrowDropDown, Cancel, Edit } from '@mui/icons-material';
import {
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  styled,
  TextField,
  TextFieldProps,
  Tooltip,
} from '@mui/material';
import { uniq } from 'lodash';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import {
  FieldPath,
  FieldPathValues,
  FieldValues,
  useController,
  UseControllerProps,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { useDebouncedCallback } from 'use-debounce';
import { SpanNoWrap } from '../styled';

/* Styled components */
const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-adornedStart': {
    paddingLeft: theme.spacing(1),
  },
}));

/* Component */

// TODO Add hidden field to hide field

interface Props<
  TFieldValues extends FieldValues,
  TFieldNames extends readonly FieldPath<TFieldValues>[],
  TName extends FieldPath<TFieldValues>,
  TCustomValues extends readonly any[],
> extends UseControllerProps<TFieldValues, TName> {
  watchFields: readonly [...TFieldNames];
  customValues?: TCustomValues;
  editedFieldsName?: FieldPath<TFieldValues>;
  compute: (
    values: FieldPathValues<TFieldValues, TFieldNames>,
    customValues?: TCustomValues,
  ) => any;
  computeTooltip?: (
    values: FieldPathValues<TFieldValues, TFieldNames>,
    fieldValue?: any,
    customValues?: TCustomValues,
  ) => ReactNode;
  forcedError?: string;
  computeIsHidden?: (
    values: FieldPathValues<TFieldValues, TFieldNames>,
    customValues?: TCustomValues,
  ) => boolean;
}

export const ComputedTextFieldForm = <
  TFieldValues extends FieldValues,
  TFieldNames extends readonly FieldPath<TFieldValues>[],
  TName extends FieldPath<TFieldValues>,
  TCustomValues extends readonly any[],
>({
  name,
  control,
  watchFields,
  compute,
  computeTooltip,
  rules,
  shouldUnregister,
  defaultValue,
  customValues,
  editedFieldsName,
  forcedError,
  computeIsHidden,
  ...props
}: Props<TFieldValues, TFieldNames, TName, TCustomValues> & TextFieldProps) => {
  const {
    field,
    fieldState: { error },
  } = useController<TFieldValues, TName>({
    name,
    control,
    rules,
    shouldUnregister,
    defaultValue,
  });
  const { setValue, getValues } = useFormContext();
  const [initialized, setInitialized] = useState(false);
  const [manualOverwrite, setManualOverwrite] = useState(false);
  const [iconHovered, setIconHovered] = useState(false);
  const [computeTooltipValue, setComputeTooltipValue] = useState<ReactNode>('');
  const [isHidden, setIsHidden] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleUpdateEditedFieldsArray = (isManualOverwrite: boolean) => {
    if (editedFieldsName) {
      let editedFieldsArray = getValues(editedFieldsName);
      if (editedFieldsArray) {
        if (isManualOverwrite) {
          editedFieldsArray = uniq([
            ...editedFieldsArray,
            name,
          ]) as typeof editedFieldsArray;
        } else {
          editedFieldsArray = editedFieldsArray.filter(
            (field: string) => field !== name,
          );
        }
        setValue(editedFieldsName, editedFieldsArray);
      }
    }
  };

  const handleSetManualOverwrite = (value: boolean) => {
    if (value !== manualOverwrite) {
      setManualOverwrite(value);
      handleUpdateEditedFieldsArray(value);
    }
  };

  const handleResetManualOverwrite = (value: number) => {
    handleSetManualOverwrite(false);
    setIconHovered(false);
    field.onChange(value);
    if (computeTooltip) {
      setComputeTooltipValue(
        computeTooltip(watchValues, undefined, customValues),
      );
    }
  };

  const watchValues = useWatch({
    control,
    name: watchFields,
  });
  const memoizedCustomValues = useMemo(() => customValues, [customValues]);

  useEffect(() => {
    const computedValue = compute(watchValues, memoizedCustomValues);
    if (computeIsHidden) {
      setIsHidden(computeIsHidden(watchValues, memoizedCustomValues));
    }
    if (computeTooltip) {
      setComputeTooltipValue(
        computeTooltip(watchValues, field.value, memoizedCustomValues),
      );
    }
    const values = getValues();
    const isEdited =
      editedFieldsName && Array.isArray(values[editedFieldsName])
        ? values[editedFieldsName].includes(name)
        : false;
    if (
      computedValue?.toString() !== field.value?.toString() &&
      field.value !== undefined &&
      isEdited
    ) {
      handleSetManualOverwrite(true);
    }
    setInitialized(true);
  }, []);

  const computedValue = compute(watchValues, memoizedCustomValues);
  const isNumberArray =
    Array.isArray(computedValue) &&
    computedValue.every((v) => typeof v === 'number');

  const middleIndex = isNumberArray ? Math.floor(computedValue.length / 2) : 0;
  const middleValue = isNumberArray ? computedValue[middleIndex] : undefined;

  useEffect(() => {
    if (initialized) {
      if (isNumberArray && !manualOverwrite) {
        if (middleValue?.toString() !== field.value?.toString()) {
          field.onChange(middleValue);
        }
      } else if (!manualOverwrite || field.value === undefined) {
        const computedValue = compute(watchValues, memoizedCustomValues);
        if (computedValue?.toString() !== field.value?.toString()) {
          field.onChange(computedValue);
        }
      }
      if (computeIsHidden) {
        setIsHidden(computeIsHidden(watchValues, memoizedCustomValues));
      }
      if (computeTooltip) {
        setComputeTooltipValue(
          computeTooltip(watchValues, computedValue, memoizedCustomValues),
        );
      }
    }
  }, [
    watchValues,
    initialized,
    memoizedCustomValues,
    isNumberArray,
    middleValue,
  ]);

  useEffect(() => {
    if (initialized && manualOverwrite && computeTooltip) {
      setComputeTooltipValue(
        computeTooltip(watchValues, field.value, memoizedCustomValues),
      );
    }
  }, [watchValues, initialized, field.value, memoizedCustomValues]);

  const handleManualOverwrite = useDebouncedCallback(() => {
    if (!manualOverwrite) {
      handleSetManualOverwrite(true);
    }
  });

  const onChangeCallback = useDebouncedCallback((value: any) =>
    field.onChange(value),
  );

  const startAdornment = (
    <InputAdornment position="start">
      <Tooltip
        title={
          manualOverwrite
            ? 'Vous avez renseigné ce champ manuellement. Supprimer ?'
            : ''
        }
        arrow
      >
        <IconButton
          style={{
            visibility: !manualOverwrite ? 'hidden' : 'visible',
          }}
          size="small"
          edge="start"
          color="primary"
          onClick={() => {
            if (manualOverwrite) {
              handleResetManualOverwrite(
                isNumberArray
                  ? middleValue
                  : compute(watchValues, customValues),
              );
            }
          }}
          onMouseEnter={() => manualOverwrite && setIconHovered(true)}
          onMouseLeave={() => manualOverwrite && setIconHovered(false)}
        >
          {iconHovered ? <Cancel /> : <Edit />}
        </IconButton>
      </Tooltip>
    </InputAdornment>
  );

  const endAdornment = (
    <InputAdornment position="end">
      <IconButton
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        disabled={!isNumberArray}
      >
        <ArrowDropDown />
      </IconButton>
    </InputAdornment>
  );

  return (
    <Tooltip
      title={forcedError || error?.message ? '' : computeTooltipValue}
      placement="top"
    >
      <SpanNoWrap>
        <Tooltip title={forcedError || error?.message || ''}>
          {isNumberArray ? (
            <>
              <StyledTextField
                {...field}
                {...props}
                hidden={props.hidden || isHidden}
                error={!!forcedError || !!error}
                value={field.value}
                onChange={onChangeCallback}
                onInput={handleManualOverwrite}
                InputProps={{
                  ...props.InputProps,
                  startAdornment,
                  endAdornment,
                  sx: {
                    ...props.InputProps?.sx,
                    ...(manualOverwrite && { color: 'primary.main' }),
                    width: 240,
                  },
                }}
              />
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
              >
                {computedValue.map((value: number) => (
                  <MenuItem
                    key={value}
                    onClick={() => {
                      handleResetManualOverwrite(value);
                      setAnchorEl(null);
                    }}
                  >
                    {value}
                  </MenuItem>
                ))}
              </Menu>
            </>
          ) : (
            <StyledTextField
              {...field}
              {...props}
              hidden={props.hidden || isHidden}
              error={!!forcedError || !!error}
              onChange={onChangeCallback}
              onInput={handleManualOverwrite}
              InputProps={{
                ...props.InputProps,
                startAdornment,
                sx: {
                  ...props.InputProps?.sx,
                  ...(manualOverwrite && { color: 'primary.main' }),
                },
              }}
            />
          )}
        </Tooltip>
      </SpanNoWrap>
    </Tooltip>
  );
};
